import { useEffect, useMemo, useRef, useState } from 'react';
import { useFeature } from '../../lib/features';
import { FeatureGate } from '../../components/FeatureGate';
import { useNavigate } from 'react-router-dom';
import { Loader2, NotebookText, Pencil, Trash2, Sparkles, X, RotateCcw, ChevronRight, Activity, GitBranch } from 'lucide-react';
import { useChannelFeed, useStore } from '../../store/StoreContext';
import { AppCard } from '../../components/ui/AppCard';
import { IconButton } from '../../components/ui/IconButton';
import { EmptyState } from '../../components/ui/EmptyState';
import { FormSection } from '../../components/ui/FormSection';
import { MarkdownReportText } from '../../components/reports/MarkdownReportText';
import { TextArea } from '../../components/ui/TextField';
import { Button } from '../../components/ui/Button';
import { AttachmentView } from '../../components/ui/AttachmentView';
import { AdditionalChannelsField } from '../../components/channels/AdditionalChannelsField';
import { ConfidenceBadge } from '../../components/reports/ConfidenceBadge';
import { EvidenceSection } from '../../components/reports/EvidenceSection';
import { AutoReportsCard } from '../../components/reports/AutoReportsCard';
import { ChannelQuestionsSection } from '../../components/reports/ChannelQuestionsSection';
import { useChannelMembership, useChannelMemberPermissions } from '../../lib/useChannelMembership';
import { parseEvidenceReport, resolveConfidenceLevel } from '../../lib/evidenceReport';
import {
  AI_PROVIDER_NEEDS_KEY,
  AI_PROVIDER_LABEL,
  AI_REPORT_PERIODS,
  REPORT_TYPE,
  REPORT_JOB_ACTIVE,
  type AiReportPeriodDays,
  type AiReportUsage,
  type ChannelRef,
  type ReportJob,
  type ReportJobStatus,
} from '../../types';
import { fetchAiKeyStatus, fetchAiReportUsage, setReportSections } from '../../api/reportApi';
import { fetchChannelReports, updateReport, deleteReport, type ChannelReport } from '../../api/dataApi';
import { usePersistedState } from '../../lib/usePersistedState';

// Kept in sync by hand with backend/internal/report/access.go's
// MaxAdditionalChannels.
const MAX_ADDITIONAL_CHANNELS = 4;

// How many job rows to show before "Load older jobs" / scroll reveals
// more. Kept small so a long job history never crowds out the reports.
const JOBS_PAGE = 3;

function formatDate(iso: string) {
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}


const JOB_STATUS: Record<ReportJobStatus, { label: string; dot: string }> = {
  queued: { label: 'Queued', dot: 'bg-black/30 dark:bg-white/30' },
  running: { label: 'Running', dot: 'bg-blue-500 animate-pulse' },
  done: { label: 'Done', dot: 'bg-green-500' },
  failed: { label: 'Failed', dot: 'bg-red-500' },
  stopped: { label: 'Stopped', dot: 'bg-black/30 dark:bg-white/30' },
};

// A report is just a message tagged type: REPORT_TYPE - this screen is a
// filtered view of c.messages. The "Generate AI report" panel (owner only)
// queues a job on the backend worker (POST /channels/{nickname}/report-jobs);
// the job list below it updates live over the Cue "rj" relay and lets the
// owner Stop a queued/running job or Rerun a finished one.
export function ReportsScreen({ channelId }: { channelId: string }) {
  const navigate = useNavigate();
  const aiOn = useFeature('reports.ai');
  const insightsOn = useFeature('analytics.insights');
  const {
    channel,
    sendMessage,
    isChannelOwner,
    aiSettings,
    reportJobs,
    reportJobsMeta,
    loadReportJobs,
    loadMoreReportJobs,
    enqueueReportJob,
    cancelReportJob,
    manualRefresh,
    getReportAdditionalChannels,
    setReportAdditionalChannels,
  } = useStore();
  const c = channel(channelId);
  const feedMessages = useChannelFeed(channelId).messages;
  const [text, setText] = useState('');
  const membership = useChannelMembership(c);
  const perms = useChannelMemberPermissions(c);
  const owner = !!c && isChannelOwner(c);
  const canPost =
    !!c && (owner || (membership === 'accepted' && perms.postMessages && perms.sendReports));

  // Inline report editing (owner-only, see updateReport's comment on why
  // this is keyed by the report's own id rather than its public
  // permalink number - a manually-typed or pre-numbering report has no
  // number at all). Kept as a local overlay rather than refetching the
  // channel's feed after a save - `reports` below is derived from the
  // store's own feedMessages, which this screen doesn't own and
  // shouldn't force-refresh just for its own edit.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [editedTexts, setEditedTexts] = useState<Record<string, string>>({});
  // Section show/hide lives on the backend (public pages react to it); this
  // map just shows a toggle immediately while the save is in flight.
  const [hiddenOverrides, setHiddenOverrides] = useState<Record<string, string[]>>({});
  const hiddenFor = (reportId: string, fromServer?: string[]) => hiddenOverrides[reportId] ?? fromServer ?? [];
  const toggleSections = (reportId: string, fromServer: string[] | undefined, next: string[]) => {
    if (!c) return;
    const before = hiddenFor(reportId, fromServer);
    setHiddenOverrides((prev) => ({ ...prev, [reportId]: next }));
    setReportSections(c.nickname, reportId, next).catch(() =>
      setHiddenOverrides((prev) => ({ ...prev, [reportId]: before }))
    );
  };

  const startEdit = (reportId: string, currentText: string) => {
    setEditingId(reportId);
    setEditDraft(editedTexts[reportId] ?? currentText);
    setEditError('');
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditError('');
  };
  const saveEdit = async (reportId: string) => {
    if (!c) return;
    setEditSaving(true);
    setEditError('');
    try {
      await updateReport(c.nickname, reportId, editDraft);
      setEditedTexts((prev) => ({ ...prev, [reportId]: editDraft }));
      setEditingId(null);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Unable to save this report.');
    } finally {
      setEditSaving(false);
    }
  };

  const [periodDays, setPeriodDays] = usePersistedState<AiReportPeriodDays>('reports.periodDays', 30, (v: unknown) => AI_REPORT_PERIODS.some((p) => p.days === v));
  // Extra channels (own or public) to fold into the next queued report as
  // a Multi-Channel Report - see CLAUDE_ZMIST_TECHNICAL_PLAN.md. Kept in
  // the store (keyed by channelId), not screen-local useState: this screen
  // unmounts/remounts on every tab switch and every round trip to
  // /channels/pick, and a local-state selection used to silently reset to
  // empty on each of those - the store survives all of them for free. See
  // ChartScreen.tsx's identical pattern for the chart-comparison sibling.
  const additionalChannels = getReportAdditionalChannels(channelId);
  const setAdditionalChannels = (channels: ChannelRef[]) => setReportAdditionalChannels(channelId, channels);
  const [queueing, setQueueing] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [usage, setUsage] = useState<AiReportUsage | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Job rows are collapsed to a one-liner; tapping one expands it to show
  // timestamps and the run's error / success detail.
  const [expandedJobs, setExpandedJobs] = useState<Set<string>>(new Set());
  const toggleJob = (id: string) =>
    setExpandedJobs((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const jobs = useMemo(
    () => (reportJobs[channelId] ?? []).slice().sort((a, b) => b.enqueuedAt - a.enqueuedAt),
    [reportJobs, channelId]
  );
  const jobsMeta = reportJobsMeta[channelId];
  // What the queue is doing right now, shown beside the reports themselves so
  // nobody has to open Settings to see that a request went through.
  const activeJobs = jobs.filter((j) => (REPORT_JOB_ACTIVE as ReportJobStatus[]).includes(j.status));
  const newest = jobs[0];
  const lastFailed = newest && newest.status === 'failed' ? newest : null;

  // Show only the newest JOBS_PAGE rows; "Load older jobs" / scrolling near
  // the bottom reveals JOBS_PAGE more and, once the locally-loaded set runs
  // out, asks the store for the next backend page.
  const [visibleJobs, setVisibleJobs] = useState(JOBS_PAGE);
  useEffect(() => setVisibleJobs(JOBS_PAGE), [channelId]);
  const shownJobs = jobs.slice(0, visibleJobs);
  const totalJobs = jobsMeta?.total ?? jobs.length;
  const canShowMoreJobs = visibleJobs < jobs.length || !!jobsMeta?.hasMore;

  const showMoreJobs = () => {
    if (jobsMeta?.loadingMore) return;
    const next = visibleJobs + JOBS_PAGE;
    setVisibleJobs(next);
    if (next > jobs.length && jobsMeta?.hasMore) loadMoreReportJobs(channelId);
  };

  const onJobsScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!canShowMoreJobs || jobsMeta?.loadingMore) return;
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) showMoreJobs();
  };

  // Pull the job queue + free-report usage once when the panel opens. The
  // queue then advances live over the Cue "rj" relay AND, as a fallback
  // for when that relay never lands, a 5s poll in StoreContext.tsx while
  // any job is still queued/running. The owner can also hit Refresh below
  // to force a re-pull. Deliberately does NOT depend on loadReportJobs:
  // that closure changes on every store update (it lives off
  // `reportJobs`), so keeping it here would re-fire the effect after its
  // own setReportJobs and hammer the endpoint. Skipped entirely in
  // manual-refresh mode (Settings > Connection) - Refresh is always shown
  // there instead.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!owner || manualRefresh) return;
    fetchAiReportUsage()
      .then(setUsage)
      .catch(() => setUsage(null));
    loadReportJobs(channelId);
  }, [owner, channelId, manualRefresh]);

  const refresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await Promise.all([
        loadReportJobs(channelId),
        fetchAiReportUsage().then(setUsage).catch(() => {}),
      ]);
    } finally {
      setRefreshing(false);
    }
  };

  // The reports come from their own endpoint (GET /signals/{id}/reports):
  // only reports, newest first, each with its position in the channel. Until
  // that first page lands - or if it fails - the reports already in the
  // channel feed are shown instead, so the tab is never blank.
  const REPORTS_PAGE = 20;
  type ReportRow = Pick<ChannelReport, 'id' | 'sender' | 'text' | 'date' | 'number' | 'edited' | 'confidence' | 'confidenceFactors' | 'additionalChannelIds' | 'attachment' | 'hiddenSections'> &
    Partial<Pick<ChannelReport, 'position'>>;
  const [serverReports, setServerReports] = useState<ChannelReport[] | null>(null);
  const [reportsTotal, setReportsTotal] = useState(0);
  const [totalSignals, setTotalSignals] = useState(0);
  const [reportsHasMore, setReportsHasMore] = useState(false);
  const [loadingMoreReports, setLoadingMoreReports] = useState(false);
  const loadedCountRef = useRef(0);

  const feedReports = useMemo(
    () => feedMessages.filter((m) => m.type === REPORT_TYPE).sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0)),
    [feedMessages]
  );
  // Changes whenever a report is added/removed in the live feed - the cue to
  // refresh the endpoint's first page (positions are as of the last fetch - the endpoint walks the channel's history, so it isn't re-called for every signal).
  const feedReportSig = `${feedReports.length}:${feedReports[0]?.id ?? ''}`;

  useEffect(() => {
    let cancelled = false;
    // Re-fetch as many reports as are already on screen (at least one page).
    const want = Math.min(Math.max(loadedCountRef.current, REPORTS_PAGE), 200);
    fetchChannelReports(channelId, 0, want)
      .then((page) => {
        if (cancelled) return;
        setServerReports(page.reports);
        loadedCountRef.current = page.reports.length;
        setReportsTotal(page.total);
        setTotalSignals(page.totalSignals);
        setReportsHasMore(page.hasMore);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [channelId, feedReportSig]);
  useEffect(() => {
    setServerReports(null);
    loadedCountRef.current = 0;
  }, [channelId]);

  const loadOlderReports = async () => {
    if (loadingMoreReports || !serverReports) return;
    setLoadingMoreReports(true);
    try {
      const page = await fetchChannelReports(channelId, serverReports.length, REPORTS_PAGE);
      setServerReports((prev) => {
        const seen = new Set((prev ?? []).map((r) => r.id));
        const merged = [...(prev ?? []), ...page.reports.filter((r) => !seen.has(r.id))];
        loadedCountRef.current = merged.length;
        return merged;
      });
      setReportsTotal(page.total);
      setReportsHasMore(page.hasMore);
    } catch {
      /* keep what is already shown */
    } finally {
      setLoadingMoreReports(false);
    }
  };

  // Reports the owner deleted this session: hidden at once, the server is the truth on reload.
  const [deletedIds, setDeletedIds] = useState<string[]>([]);
  const [deleteError, setDeleteError] = useState('');
  const reports: ReportRow[] = (serverReports ?? feedReports).filter((r) => !deletedIds.includes(r.id));
  const removeReport = async (reportId: string) => {
    if (!c || !window.confirm("Delete this report? This can't be undone.")) return;
    setDeleteError('');
    try {
      await deleteReport(c.nickname, reportId);
      setDeletedIds((prev) => [...prev, reportId]);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Unable to delete this report.');
    }
  };

  // A key saved on the account is never sent back to the browser (and the server
  // uses it when a request carries none), so ask which providers have one.
  const [savedProviders, setSavedProviders] = useState<string[]>([]);
  useEffect(() => {
    fetchAiKeyStatus()
      .then((list) => setSavedProviders(list.filter((k) => k.saved).map((k) => k.provider)))
      .catch(() => {});
  }, []);
  if (!c) return null;

  const send = () => {
    if (!text.trim()) return;
    sendMessage(text.trim(), c.id, REPORT_TYPE);
    setText('');
  };

  // The composer's primary action: the typed text goes to the AI as a question.
  const askAi = () => {
    const q = text.trim();
    if (!q || queueing) return;
    setText('');
    void queue({ mode: 'questions', questions: [q] });
  };

  const provider = aiSettings.provider;
  const providerLabel = AI_PROVIDER_LABEL[provider];
  const providerKey = (aiSettings.apiKeys[provider] ?? '').trim();
  const usingOwnKey = !!providerKey || savedProviders.includes(provider);
  const providerNeedsKey = AI_PROVIDER_NEEDS_KEY[provider] && !usingOwnKey;
  const onFreeAllowance = !AI_PROVIDER_NEEDS_KEY[provider] && !usingOwnKey;
  // usage.remaining -1 means unlimited (a paid tier - see #11 pricing
  // enforcement / report.FreeLimitFor) - never treat that as exhausted.
  const outOfFreeReports = onFreeAllowance && !!usage && usage.remaining !== -1 && usage.remaining <= 0;

  const queue = async (
    overrides?: {
      provider?: typeof provider;
      periodDays?: AiReportPeriodDays;
      additionalChannelIds?: string[];
      mode?: 'questions';
      questions?: string[];
    }
  ) => {
    setQueueing(true);
    setAiError(null);
    try {
      await enqueueReportJob(c.id, {
        provider: overrides?.provider ?? provider,
        periodDays: overrides?.periodDays ?? periodDays,
        // Priority is always "normal" now - the per-job priority control
        // was removed; the backend still accepts the field.
        priority: 'normal',
        apiKey: providerKey || undefined,
        additionalChannelIds: overrides?.additionalChannelIds ?? additionalChannels.map((ch) => ch.id),
        mode: overrides?.mode,
        questions: overrides?.questions,
      });
      fetchAiReportUsage().then(setUsage).catch(() => {});
    } catch (err) {
      setAiError(err instanceof Error ? err.message : 'Could not queue the report.');
    } finally {
      setQueueing(false);
    }
  };

  const rerun = (job: ReportJob) =>
    queue({
      provider: job.provider,
      periodDays: (AI_REPORT_PERIODS.some((p) => p.days === job.periodDays)
        ? job.periodDays
        : 30) as AiReportPeriodDays,
      // Carry the original job's Multi-Channel Report selection through -
      // without this, rerunning a multi-channel report silently fell back
      // to whatever (usually empty) additionalChannels the panel's own
      // state happened to hold, degrading it to single-channel.
      additionalChannelIds: job.additionalChannelIds ?? [],
    });

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 pb-3">
        <div className="flex-1">
          <span className="font-semibold block">Reports</span>
        </div>
        {insightsOn && <IconButton icon={Activity} onClick={() => navigate(`/channels/${c.id}/insights`)} aria-label="Insights" />}
        {/* No in-app nav link reached this screen before (the route only
            worked by typing the URL directly) - see WorkflowListScreen.tsx /
            WorkflowScreen.tsx's own fixes for this same bug report's other
            half (save/persistence). Owner-gated like AI settings - workflows
            are a channel-management tool, not something a visitor/member
            needs. */}
        {owner && <IconButton icon={GitBranch} onClick={() => navigate(`/channels/${c.id}/workflows`)} aria-label="Workflows" />}
        {aiOn && <IconButton icon={Sparkles} onClick={() => navigate(`/channels/${c.id}/ai-settings`)} aria-label="AI settings" />}
      </div>

      {/* Everything between the title and the composer scrolls together, so
          opening Settings or Manage questions never pushes the composer off
          screen - it stays pinned below. */}
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col">
      {owner && (
        <div className="pb-3">
          <AdditionalChannelsField
            channels={additionalChannels}
            onChange={setAdditionalChannels}
            excludeId={c.id}
            max={MAX_ADDITIONAL_CHANNELS}
            pickerTitle="Add channels to this report"
            noun="report"
            info={`Add other channels to compare with ${c.title}. Reports and questions then cover all of them, and the Charts tab shows the same channels.`}
          />
        </div>
      )}

      {owner && aiOn && (
        <AppCard className="flex flex-col gap-2.5 mb-3">
          <div className="flex items-center gap-2">
            <Sparkles size={15} className="text-blue-500" />
            <span className="text-sm font-semibold">Generate AI report</span>
            <button
              type="button"
              onClick={() => navigate(`/channels/${c.id}/ai-settings`)}
              className="ml-auto text-[11px] text-blue-600 hover:underline"
            >
              via {providerLabel} · change
            </button>
            {manualRefresh && (
              <button
                type="button"
                onClick={refresh}
                disabled={refreshing}
                aria-label="Refresh job queue"
                className="inline-flex items-center gap-1 text-[11px] text-black/50 dark:text-white/50 hover:text-blue-600 disabled:opacity-50"
              >
                <RotateCcw size={12} className={refreshing ? 'animate-spin' : ''} />
                Refresh
              </button>
            )}
          </div>

          <FormSection title="Settings" collapsible id="report-settings" defaultOpen={false}>
            <label className="flex flex-col gap-1 min-w-0">
              <span className="text-[11px] font-medium text-black/50 dark:text-white/50">Period</span>
              <select
                value={periodDays}
                onChange={(e) => setPeriodDays(Number(e.target.value) as AiReportPeriodDays)}
                disabled={queueing}
                className="rounded-lg border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-3 py-2 text-sm outline-none focus:border-blue-500"
              >
                {AI_REPORT_PERIODS.map((p) => (
                  <option key={p.days} value={p.days}>
                    {p.label}
                  </option>
                ))}
              </select>
            </label>

            {jobs.length > 0 && (
              <div className="border-t border-black/10 dark:border-white/10 -mx-3.5 px-3.5 pt-1 -mb-1">
                <div className="flex items-center justify-between text-[11px] text-black/40 dark:text-white/40 pb-1">
                  <span>Recent jobs</span>
                  <span>{totalJobs > shownJobs.length ? `${shownJobs.length} of ${totalJobs}` : `${shownJobs.length}`}</span>
                </div>
                {/* Capped to ~3 rows and scrolls internally so a long job
                    history never pushes the reports below it off screen;
                    scrolling to the bottom reveals more. */}
                <div
                  onScroll={onJobsScroll}
                  className="flex flex-col divide-y divide-black/5 dark:divide-white/5 max-h-40 overflow-y-auto"
                >
                  {shownJobs.map((job) => {
                    const meta = JOB_STATUS[job.status];
                    const active = (REPORT_JOB_ACTIVE as ReportJobStatus[]).includes(job.status);
                    const open = expandedJobs.has(job.id);
                    return (
                      <div key={job.id} className="text-xs">
                        <div className="flex items-center gap-2 py-2">
                          <button
                            type="button"
                            onClick={() => toggleJob(job.id)}
                            className="flex items-center gap-2 min-w-0 flex-1 text-left"
                            aria-expanded={open}
                          >
                            <ChevronRight
                              size={12}
                              className={`shrink-0 text-black/30 dark:text-white/30 transition-transform ${open ? 'rotate-90' : ''}`}
                            />
                            <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${meta.dot}`} />
                            <span className="font-medium text-black/70 dark:text-white/70 w-14 shrink-0">{meta.label}</span>
                            <span className="text-black/50 dark:text-white/50 truncate">
                              {AI_PROVIDER_LABEL[job.provider] ?? job.provider} · {job.periodDays}d
                              <span className="text-black/30 dark:text-white/30"> · {formatDate(new Date(job.enqueuedAt).toISOString())}</span>
                            </span>
                          </button>
                          <span className="ml-auto shrink-0">
                            {active ? (
                              <button
                                type="button"
                                onClick={() => cancelReportJob(c.id, job.id)}
                                className="inline-flex items-center gap-1 rounded-md border border-black/15 dark:border-white/20 px-2 py-1 text-black/55 dark:text-white/55 hover:text-red-500 hover:border-red-400"
                              >
                                <X size={12} /> Stop
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => rerun(job)}
                                disabled={queueing}
                                className="inline-flex items-center gap-1 rounded-md border border-black/15 dark:border-white/20 px-2 py-1 text-black/55 dark:text-white/55 hover:text-blue-600 hover:border-blue-400 disabled:opacity-50"
                              >
                                <RotateCcw size={12} /> Rerun
                              </button>
                            )}
                          </span>
                        </div>
                        {open && (
                          <div className="pb-2 pl-6 flex flex-col gap-0.5 text-[11px] text-black/50 dark:text-white/50">
                            <span>Queued {formatDate(new Date(job.enqueuedAt).toISOString())}</span>
                            {!!job.startedAt && <span>Started {formatDate(new Date(job.startedAt).toISOString())}</span>}
                            {!!job.finishedAt && <span>Finished {formatDate(new Date(job.finishedAt).toISOString())}</span>}
                            {job.keySource && (
                              <span>
                                {job.keySource === 'user'
                                  ? 'Ran with your API key'
                                  : job.keySource === 'local'
                                    ? 'Ran on the local model'
                                    : 'Used a free report'}
                              </span>
                            )}
                            {job.status === 'done' && (
                              <span className="text-green-600 dark:text-green-500">Report posted to the channel below.</span>
                            )}
                            {job.status === 'stopped' && <span>Stopped before it finished.</span>}
                            {job.error && <span className="text-red-500 whitespace-pre-wrap">{job.error}</span>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {jobsMeta?.loadingMore && (
                    <div className="py-2 text-center text-[11px] text-black/40 dark:text-white/40">Loading…</div>
                  )}
                  {canShowMoreJobs && !jobsMeta?.loadingMore && (
                    <button
                      type="button"
                      onClick={showMoreJobs}
                      className="py-2 text-center text-[11px] text-blue-600 hover:underline"
                    >
                      Load older jobs
                    </button>
                  )}
                </div>
              </div>
            )}

            {owner && c && <AutoReportsCard channelId={c.id} />}
          </FormSection>

          <FeatureGate feature="reports.ai" fallback={null}>
          <ChannelQuestionsSection
            nickname={c.nickname}
            provider={provider}
            apiKey={providerKey || undefined}
            asking={queueing}
            onAsk={(questions) => queue({ mode: 'questions', questions })}
          />
          </FeatureGate>

          <span className="text-[11px] text-black/40 dark:text-white/40">
            {providerNeedsKey
              ? `Add your ${providerLabel} API key in AI settings to generate.`
              : usingOwnKey
                ? `Using your own ${providerLabel} key — no limit.`
                : usage
                  ? usage.remaining === -1
                    ? 'Unlimited on your plan.'
                    : outOfFreeReports
                      ? 'No free reports left today. Resets tomorrow (UTC), or switch providers in AI settings.'
                      : `${usage.remaining} of ${usage.limit} free reports left today.`
                  : 'A few free reports per day are included; switch providers in AI settings for unlimited.'}
          </span>
          {aiError && <span className="text-xs text-red-500">{aiError}</span>}
        </AppCard>
      )}

      {owner && activeJobs.length + (lastFailed ? 1 : 0) > 0 && (
        <div role="status" aria-live="polite" aria-label="Report queue" className="flex flex-col gap-1.5 mb-3">
          {activeJobs.map((job) => (
            <div key={job.id} className="flex items-center gap-2 rounded-xl border border-blue-500/30 bg-blue-500/5 px-3 py-2 text-xs">
              <Loader2 size={14} className="animate-spin shrink-0 text-blue-600" aria-hidden />
              <span className="font-medium">{job.status === 'running' ? 'Running' : 'In the queue'}</span>
              <span className="text-black/55 dark:text-white/55 truncate">
                {job.mode === 'questions' ? 'AI answers' : job.mode === 'suggest' ? 'Suggesting questions' : 'AI report'} · {AI_PROVIDER_LABEL[job.provider] ?? job.provider}
              </span>
              <button
                type="button"
                onClick={() => cancelReportJob(c.id, job.id)}
                className="ml-auto inline-flex items-center gap-1 rounded-md border border-black/15 dark:border-white/20 px-2 py-1 text-black/55 dark:text-white/55 hover:text-red-500 hover:border-red-400"
              >
                <X size={12} /> Stop
              </button>
            </div>
          ))}
          {lastFailed && (
            <div className="flex items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/5 px-3 py-2 text-xs text-red-600 dark:text-red-400">
              <span className="font-medium shrink-0">Failed</span>
              <span className="whitespace-pre-wrap break-words min-w-0">{lastFailed.error || 'The AI request did not finish.'}</span>
            </div>
          )}
        </div>
      )}

      {deleteError && <p role="alert" className="text-xs text-red-500 mb-2">{deleteError}</p>}
      <div className="flex flex-col gap-3 pb-3">
        {reports.length === 0 ? (
          <EmptyState icon={NotebookText} title="No reports" subtitle="Add the first note." />
        ) : (
          reports.map((report) => {
            const effectiveText = editedTexts[report.id] ?? report.text;
            const isEdited = report.edited || editedTexts[report.id] !== undefined;
            const isEditingThis = editingId === report.id;
            // Only a Multi-Channel Report was ever prompted with the
            // structured "## Header" format (buildMultiPrompt) - an
            // ordinary single-channel or "Ask questions" report correctly
            // fails to parse and falls through to the plain
            // MarkdownReportText rendering below, same as it always has.
            const isMultiChannel = !!report.additionalChannelIds?.length;
            const evidence = isMultiChannel ? parseEvidenceReport(effectiveText) : null;
            const confidenceLevel = resolveConfidenceLevel(report.confidence, evidence?.confidenceLevel ?? null);
            return (
              <AppCard key={report.id} className="flex flex-col gap-1.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold text-black/50 dark:text-white/50">{report.sender}</span>
                  {confidenceLevel && <ConfidenceBadge level={confidenceLevel} />}
                  {isMultiChannel && (
                    <span className="text-[11px] text-black/40 dark:text-white/40">
                      + {report.additionalChannelIds!.length} channel{report.additionalChannelIds!.length === 1 ? '' : 's'}
                    </span>
                  )}
                  {report.position ? (
                    <span
                      className="text-[11px] text-black/40 dark:text-white/40"
                      title="Where this report sits among all signals in the channel, counting from the first"
                    >
                      Signal #{report.position}
                      {totalSignals ? ` of ${totalSignals}` : ''}
                    </span>
                  ) : null}
                  {report.number && (
                    <span className="text-[11px] text-black/40 dark:text-white/40">
                      Report #{report.number}
                      {isEdited ? ' · Edited' : ''}
                    </span>
                  )}
                  {owner && !isEditingThis && (
                    <button
                      type="button"
                      onClick={() => startEdit(report.id, report.text)}
                      aria-label="Edit report"
                      title="Edit"
                      className="ml-auto p-1 rounded-md text-blue-600 dark:text-blue-400 hover:bg-black/5 dark:hover:bg-white/10"
                    >
                      <Pencil size={14} aria-hidden />
                    </button>
                  )}
                  {owner && !isEditingThis && (
                    <button
                      type="button"
                      onClick={() => void removeReport(report.id)}
                      aria-label="Delete report"
                      title="Delete"
                      className="p-1 rounded-md text-red-600 dark:text-red-400 hover:bg-black/5 dark:hover:bg-white/10"
                    >
                      <Trash2 size={14} aria-hidden />
                    </button>
                  )}
                </div>
                {isEditingThis ? (
                  <div className="flex flex-col gap-2">
                    <TextArea
                      value={editDraft}
                      onChange={(e) => setEditDraft(e.target.value)}
                      className="min-h-[200px] font-mono text-[13px]"
                    />
                    {editError && <span className="text-xs text-red-500">{editError}</span>}
                    <div className="flex gap-2 justify-end">
                      <Button variant="secondary" onClick={cancelEdit} disabled={editSaving}>
                        Cancel
                      </Button>
                      <Button onClick={() => saveEdit(report.id)} loading={editSaving} disabled={editSaving}>
                        Save
                      </Button>
                    </div>
                  </div>
                ) : evidence ? (
                  <div className="flex flex-col gap-2.5">
                    <EvidenceSection title="Direct answer" text={evidence.directAnswer} />
                    <EvidenceSection title="Key evidence" text={evidence.keyEvidence} />
                    <EvidenceSection title="Relationships across channels" text={evidence.relationships} />
                    <EvidenceSection
                      title={confidenceLevel ? 'Confidence reasoning' : 'Confidence'}
                      text={evidence.confidenceReason}
                    />
                    {!!report.confidenceFactors?.length && (
                      <EvidenceSection
                        title="Confidence factors (computed)"
                        text={report.confidenceFactors.map((f) => `• ${f}`).join('\n')}
                      />
                    )}
                    <EvidenceSection title="Conflicting evidence" text={evidence.conflictingEvidence} />
                    <EvidenceSection title="Suggested next question" text={evidence.nextQuestion} />
                  </div>
                ) : (
                  effectiveText.trim() && <MarkdownReportText
                      text={effectiveText}
                      className="text-[15px] flex flex-col gap-1"
                      hiddenSections={hiddenFor(report.id, report.hiddenSections)}
                      onToggleSection={owner ? (next) => toggleSections(report.id, report.hiddenSections, next) : undefined}
                    />
                )}
                {report.attachment && <AttachmentView attachment={report.attachment} />}
                <span className="text-[11px] text-black/40 dark:text-white/40">{formatDate(report.date)}</span>
              </AppCard>
            );
          })
        )}
        {serverReports && reportsHasMore && (
          <button
            type="button"
            onClick={() => void loadOlderReports()}
            disabled={loadingMoreReports}
            className="self-center rounded-lg border border-black/15 dark:border-white/20 px-3 py-1.5 text-xs text-black/60 dark:text-white/60 hover:text-blue-600 disabled:opacity-50"
          >
            {loadingMoreReports ? 'Loading…' : `Load older reports (${Math.max(reportsTotal - serverReports.length, 0)} more)`}
          </button>
        )}
      </div>

      </div>

      {membership === 'loading' ? null : canPost ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            // Enter / the primary button asks the AI (owners); anyone else
            // can only post the text as a report.
            if (owner) askAi();
            else send();
          }}
          className="flex gap-2 shrink-0 pt-2 pb-1"
        >
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={owner ? 'Write a report, or ask the AI' : 'Write a report'}
            className="flex-1 min-w-0 rounded-xl border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-3.5 py-2.5 text-[15px] outline-none focus:border-blue-500"
          />
          <button
            type={owner ? 'button' : 'submit'}
            onClick={owner ? send : undefined}
            className={
              owner
                ? 'rounded-xl px-4 py-2.5 border border-black/15 dark:border-white/20 font-medium hover:bg-black/5 dark:hover:bg-white/10'
                : 'rounded-xl px-4 py-2.5 bg-blue-600 text-white font-medium hover:bg-blue-700'
            }
          >
            Send
          </button>
          {owner && aiOn && (
            <button
              type="submit"
              disabled={queueing || !text.trim() || providerNeedsKey || outOfFreeReports}
              className="rounded-xl px-4 py-2.5 bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              <Sparkles size={15} aria-hidden /> Ask AI
            </button>
          )}
        </form>
      ) : membership === 'accepted' && !owner ? (
        <AppCard className="mb-2">
          <span className="text-sm text-black/60 dark:text-white/60">You're not allowed to send reports in this channel.</span>
        </AppCard>
      ) : (
        <AppCard className="mb-2">
          <span className="text-sm text-black/60 dark:text-white/60">Follow this channel to add a report.</span>
        </AppCard>
      )}
    </div>
  );
}
